/**
 * §6.3 — COMPLEXITY INDEX.
 *
 * *"Systems band: 1 → 1, 2 → 2, 3 → 3, 4-5 → 4, 6+ → 5. Interfaces band: 0-5 → 1, 6-15 → 2,
 * 16-30 → 3, 31-60 → 4, 61+ → 5. Capability complexity = 0.4 × systems band + 0.6 ×
 * interfaces band (defaults, configurable). Complexity Index = Σ (effective weight ×
 * capability complexity), range 1.0-5.0. Proposed Complexity (Low, Medium, High, Very High) is
 * stored separately. A reason is required when it differs from the calculated value."*
 *
 * ── D-004, AND EXACTLY HOW FAR IT REACHES ──────────────────────────────────────────────
 *
 * | §6.3 as written (1-5) | This module (1-100%) |
 * |---|---|
 * | systems band 1,2,3,4,5 | 20%, 40%, 60%, 80%, 100% |
 * | interfaces band 1,2,3,4,5 | 20%, 40%, 60%, 80%, 100% |
 * | Complexity Index, range 1.0-5.0 | range 1-100% |
 *
 * D-004: *"The band BOUNDARIES are unchanged — systems 1/2/3/4-5/6+, interfaces 0-5/6-15/
 * 16-30/31-60/61+. Only the value each band yields changes. The weighting arithmetic is
 * unchanged: capability complexity stays 0.4 × systems + 0.6 × interfaces."*
 *
 * The VALUES are configuration, passed in, defaulting to D-004's translation (which is also
 * what `0096` defaults the row to, so the database and the package cannot disagree about the
 * default).
 *
 * ── THE BOUNDARIES ARE CONFIGURATION TOO, SINCE `0109` ─────────────────────────────────
 *
 * D-004 and `0096` fixed the boundaries in this file. The owner reversed that on 2026-10-09
 * (D-004 amendment, `docs/consolidation/DECISIONS.md`): *"ensure that I could also change the
 * number of interfaces as well"*. The constants below are now the DEFAULTS — what `0109`
 * defaults the eight new columns to — and every band function takes the boundaries as a
 * parameter that falls back to them, so nothing that passes none scores any differently.
 */
import {
  assertCount,
  assertFactorPair,
  assertPctTenths,
  ONE_HUNDRED_PCT_TEN_THOUSANDTHS,
  roundHalfUp,
} from './units';

/**
 * §6.3's systems boundaries as INCLUSIVE UPPER BOUNDS of the first four bands: `1 / 2 / 3 /
 * 4-5`, with `6+` the open-ended fifth. The DEFAULT since `0109` made them configurable.
 */
export const SYSTEMS_BOUNDARIES: readonly number[] = [1, 2, 3, 5];

/** §6.3's interfaces boundaries, same form: `0-5 / 6-15 / 16-30 / 31-60`, then `61+`. Default. */
export const INTERFACES_BOUNDARIES: readonly number[] = [5, 15, 30, 60];

/** D-004's translation of 1,2,3,4,5 → 20%, 40%, 60%, 80%, 100%, in tenths. */
export const DEFAULT_BAND_VALUES_TENTHS: readonly number[] = [200, 400, 600, 800, 1000];

/**
 * Band index 0-4 for a count, against the inclusive upper bounds of the first four bands.
 *
 * Exported because §6.3's bands are also what a screen labels a row with, and a second
 * implementation of the boundary test in the browser is exactly the drift D-009 exists to stop.
 */
export function bandIndex(count: number, boundaries: readonly number[]): number {
  assertCount(count, 'bandIndex: count');
  for (let i = 0; i < boundaries.length; i++) {
    if (count <= boundaries[i]!) return i;
  }
  return boundaries.length; // the open-ended top band
}

/**
 * The systems band VALUE for a count, in tenths of a percentage point.
 *
 * A systems count of 0 is not a band §6.3 defines — its lowest band is "1". It is treated as
 * the lowest band rather than rejected, because a capability nobody runs a system for is a real
 * state in the grid (and `is_not_applicable` is how the model says so). A8's specification made
 * the same call and the two are cross-checked, so the reading is shared rather than guessed
 * twice.
 */
export function systemsBandTenths(
  systemsCount: number,
  bandValuesTenths: readonly number[] = DEFAULT_BAND_VALUES_TENTHS,
  boundaries: readonly number[] = SYSTEMS_BOUNDARIES,
): number {
  return bandValuesTenths[bandIndex(Math.max(systemsCount, 1), boundaries)]!;
}

/** The interfaces band VALUE for a count, in tenths. `0` IS a band here — §6.3's first is `0-5`. */
export function interfacesBandTenths(
  interfacesCount: number,
  bandValuesTenths: readonly number[] = DEFAULT_BAND_VALUES_TENTHS,
  boundaries: readonly number[] = INTERFACES_BOUNDARIES,
): number {
  return bandValuesTenths[bandIndex(interfacesCount, boundaries)]!;
}

export interface ComplexityFactors {
  /** §6.3's 0.4, in tenths of a percentage point: 400. Configurable per initiative. */
  readonly systemsFactorWeightTenths: number;
  /** §6.3's 0.6, in tenths: 600. The two must sum to 1000 — the DDL enforces it too. */
  readonly interfacesFactorWeightTenths: number;
  readonly systemsBandValuesTenths?: readonly number[];
  readonly interfacesBandValuesTenths?: readonly number[];
  /**
   * The inclusive UPPER bounds of bands 1-4; band 5 is open-ended. Configurable per initiative
   * since `0109` (owner, 2026-10-09: *"ensure that I could also change the number of interfaces
   * as well"*), defaulting to the constants above, so a caller that passes none scores exactly
   * as before.
   */
  readonly systemsBandBoundaries?: readonly number[];
  readonly interfacesBandBoundaries?: readonly number[];
}

/** §6.3's own defaults, which are also `scoring_config`'s column defaults (`0096`). */
export const DEFAULT_COMPLEXITY_FACTORS: ComplexityFactors = {
  systemsFactorWeightTenths: 400,
  interfacesFactorWeightTenths: 600,
};

/**
 * One capability's complexity, in TEN-THOUSANDTHS of a percentage point.
 *
 * Same derivation as the weighted overlap: two weights in tenths against two band values in
 * tenths, so the exact product is a plain integer sum and no division is needed.
 */
export function capabilityComplexityTenThousandths(
  systemsCount: number,
  interfacesCount: number,
  factors: ComplexityFactors = DEFAULT_COMPLEXITY_FACTORS,
): number {
  assertFactorPair(
    factors.systemsFactorWeightTenths,
    factors.interfacesFactorWeightTenths,
    'complexity',
  );
  const bandS = systemsBandTenths(
    systemsCount,
    factors.systemsBandValuesTenths,
    factors.systemsBandBoundaries,
  );
  const bandI = interfacesBandTenths(
    interfacesCount,
    factors.interfacesBandValuesTenths,
    factors.interfacesBandBoundaries,
  );
  return factors.systemsFactorWeightTenths * bandS + factors.interfacesFactorWeightTenths * bandI;
}

export interface ComplexityRow {
  readonly weightTenths: number;
  readonly systemsCount: number;
  readonly interfacesCount: number;
  readonly notApplicable: boolean;
}

export interface ComplexityResult {
  readonly pctTenThousandths: number;
  readonly exact: boolean;
  /** One entry per input row, in input order, so a grid can show the per-capability figure. */
  readonly perCapabilityTenThousandths: readonly number[];
}

/**
 * The Complexity Index for one assessment unit, in ten-thousandths of a percentage point.
 *
 * ⚠ **A READING, NOT A SETTLED RULE.** N/A capabilities are excluded from the sum and their
 * weight is renormalized away — the behaviour §6.2 option B has, applied here because §6.3
 * states no N/A handling of its own. The argument: a capability the unit does not have is not a
 * capability with zero complexity, and counting it as zero would make a NARROW unit look SIMPLE
 * rather than small. A8's specification made the same call and flagged it the same way, so the
 * two agree by design rather than by luck.
 *
 * It does NOT follow `scoring_config.na_option`, and that is deliberate: `na_option` is §6.2's
 * setting, named for overlap, and silently widening it to a second index would be a decision
 * taken on the owner's behalf. **Raised in A3's report as needing the owner.** Nothing in the
 * quadrant proof turns on it — `test/erp-consolidation/quadrants.ts`'s four witnesses carry no
 * N/A rows.
 */
export function complexityIndex(
  rows: readonly ComplexityRow[],
  factors: ComplexityFactors = DEFAULT_COMPLEXITY_FACTORS,
): ComplexityResult {
  const perCapabilityTenThousandths: number[] = [];
  let weightedSum = 0;
  let scoredWeightTenths = 0;

  for (const r of rows) {
    assertPctTenths(r.weightTenths, 'complexityIndex: weightTenths');
    const cx = capabilityComplexityTenThousandths(r.systemsCount, r.interfacesCount, factors);
    perCapabilityTenThousandths.push(cx);
    if (r.notApplicable) continue;
    scoredWeightTenths += r.weightTenths;
    weightedSum += r.weightTenths * cx;
  }

  if (scoredWeightTenths === 0) {
    return { pctTenThousandths: 0, exact: true, perCapabilityTenThousandths };
  }
  return {
    pctTenThousandths: roundHalfUp(weightedSum, scoredWeightTenths),
    exact: weightedSum % scoredWeightTenths === 0,
    perCapabilityTenThousandths,
  };
}

/**
 * The counts one cell contributes, with the two overrides `capability_assessment` carries.
 *
 * Same precedence rule and same reason as `effectiveOverlapTenths`: the override wins when
 * present, decided once here rather than in the grid, the scorer and every report separately.
 */
export function effectiveCounts(cell: {
  readonly systemsCount: number | null;
  readonly systemsCountOverride: number | null;
  readonly interfacesCount: number | null;
  readonly interfacesCountOverride: number | null;
}): { readonly systemsCount: number | null; readonly interfacesCount: number | null } {
  return {
    systemsCount: cell.systemsCountOverride ?? cell.systemsCount,
    interfacesCount: cell.interfacesCountOverride ?? cell.interfacesCount,
  };
}

/* ──────────────────────────────────────────────────────────────────────────────────────
   §6.3's SECOND HALF — *"Proposed Complexity (Low, Medium, High, Very High) is stored
   separately. A reason is required when it differs from the calculated value."*
   ────────────────────────────────────────────────────────────────────────────────────── */

/** `estimate.proposed_complexity`'s CHECK list, in order, verbatim. */
export const PROPOSED_COMPLEXITY_LABELS = ['Low', 'Medium', 'High', 'Very High'] as const;

export type ProposedComplexity = (typeof PROPOSED_COMPLEXITY_LABELS)[number];

/**
 * ⚠ **§6.3 DOES NOT GIVE THESE BOUNDARIES.** It names four labels and never says what range of
 * the calculated index each covers — which it has to, because *"a reason is required when it
 * differs from the calculated value"* cannot be evaluated without mapping the calculated
 * percentage onto one of the four labels.
 *
 * The default below, and why it is not quartiles: `25/50/75` would put the Medium/High line at
 * 50%, and the **only complexity boundary §6 actually states** is §6.5's `3.0` — which D-004
 * translates to **60%** precisely because *"3.0 on a 1-5 scale is the midpoint, and 60% is the
 * same midpoint expressed in the new units"*. A Medium/High line anywhere other than 60% would
 * mean a capability could be labelled "Medium" complexity and still land in a high-complexity
 * quadrant, which is the kind of quiet contradiction nobody notices until a client asks.
 *
 * So: `Low < 40% ≤ Medium < 60% ≤ High < 80% ≤ Very High`, where 40 and 80 are D-004's own band
 * values either side of the midpoint and 60 is §6.5's threshold. **Configurable**, and **flagged
 * in A3's report as needing the owner** — it is the one place in §6.3 where no reading is forced.
 */
export const DEFAULT_PROPOSED_COMPLEXITY_BOUNDARIES_TENTHS: readonly number[] = [400, 600, 800];

/**
 * The label the CALCULATED index corresponds to.
 *
 * `boundariesTenths` are the three ascending lines between the four labels, in tenths, each one
 * the INCLUSIVE lower bound of the label above it.
 */
export function calculatedComplexityLabel(
  pctTenThousandths: number,
  boundariesTenths: readonly number[] = DEFAULT_PROPOSED_COMPLEXITY_BOUNDARIES_TENTHS,
): ProposedComplexity {
  if (boundariesTenths.length !== PROPOSED_COMPLEXITY_LABELS.length - 1) {
    throw new Error(
      `calculatedComplexityLabel: expected ${PROPOSED_COMPLEXITY_LABELS.length - 1} boundaries, got ${boundariesTenths.length}`,
    );
  }
  for (let i = 0; i < boundariesTenths.length; i++) {
    const boundary = assertPctTenths(boundariesTenths[i]!, 'complexity label boundary') * 1000;
    if (pctTenThousandths < boundary) return PROPOSED_COMPLEXITY_LABELS[i]!;
  }
  return PROPOSED_COMPLEXITY_LABELS[PROPOSED_COMPLEXITY_LABELS.length - 1]!;
}

export interface ProposedComplexityCheck {
  readonly calculated: ProposedComplexity;
  readonly calculatedPctTenThousandths: number;
  readonly proposed: ProposedComplexity | null;
  readonly differs: boolean;
  readonly reasonRequired: boolean;
  readonly satisfied: boolean;
}

/**
 * §6.3's rule, as one answer: *"A reason is required when it differs from the calculated value."*
 *
 * A `null` proposal differs from nothing — the field is simply not filled in, and §6.3 says the
 * proposed value *"is stored separately"*, not that it is mandatory. A reason supplied where
 * none is required is not an error either; the spec requires the reason's PRESENCE under one
 * condition and says nothing about its absence being forbidden elsewhere.
 *
 * The database cannot make this check — `0097` says so at the column: *"enforced in the scoring
 * package, because the database cannot see the calculated side."* This is that enforcement, and
 * being in the package is what makes the screen and the route refuse on the same grounds.
 */
export function checkProposedComplexity(args: {
  readonly calculatedPctTenThousandths: number;
  readonly proposed: ProposedComplexity | null;
  readonly reason: string | null;
  readonly boundariesTenths?: readonly number[];
}): ProposedComplexityCheck {
  const calculated = calculatedComplexityLabel(
    args.calculatedPctTenThousandths,
    args.boundariesTenths ?? DEFAULT_PROPOSED_COMPLEXITY_BOUNDARIES_TENTHS,
  );
  const differs = args.proposed !== null && args.proposed !== calculated;
  const hasReason = args.reason !== null && args.reason.trim() !== '';
  return {
    calculated,
    calculatedPctTenThousandths: args.calculatedPctTenThousandths,
    proposed: args.proposed,
    differs,
    reasonRequired: differs,
    satisfied: !differs || hasReason,
  };
}

/** `100.0000%` re-exported under the name the complexity range is stated in (D-004: 1-100%). */
export const COMPLEXITY_MAX_TEN_THOUSANDTHS = ONE_HUNDRED_PCT_TEN_THOUSANDTHS;
